import { Component, Input, NgZone, OnInit, ViewChild } from '@angular/core';
import { ModuleManager } from 'src/shared/models/module-manager';
import { CoachingStatusService } from './coaching-status.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManagerMenuItemType, ModuleManagerSubMenu } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { BaseChartDirective } from 'ng2-charts';
import { resolve } from 'chart.js/helpers';
import { ChartConfiguration } from 'chart.js';
import { CoachingTypeFilterComponent } from '../../toolbar-items/coaching-type-filter/coaching-type-filter.component';
import { AssignedToMeDataObj, OwnedByMeDataObj, CoachingStatusModuleData } from './coaching-status.model';

@Component({
    selector: 'coaching-status',
    templateUrl: './coaching-status.component.html',
    styleUrls: ['./coaching-status.component.scss']
})
export class CoachingStatusComponent implements OnInit {
    @ViewChild(BaseChartDirective) chartObject: BaseChartDirective;
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    view = 0;
    coachingCategory = 0;
    state = null;
    noData = false;
    assignedToMeData: AssignedToMeDataObj;
    ownedByMeData: OwnedByMeDataObj;
    chartConfig = null;
    data: AssignedToMeDataObj | OwnedByMeDataObj;
    coachingData: CoachingStatusModuleData;
    isDataLoading = false;
    abortLoad: () => void = null;
    _chartConstants = {
        colors: [ // This list is in the order of Chart dataset backgroundColor values
            "rgba(61,150,73,1)",
            "rgba(124,82,191,1)",
            "rgba(228,179,33,1)",
            "rgba(241,102,34,1)",
            "rgba(189,32,38,1)",
            "rgba(227,28,121,1)",
            "rgba(70,58,50,1)",
        ],
        colorsLight: [ // This list is in the order of Chart dataset backgroundColor values
            "rgba(61,150,73,.5)",
            "rgba(124,82,191,.5)",
            "rgba(228,179,33,.5)",
            "rgba(241,102,34,.5)",
            "rgba(189,32,38,.5)",
            "rgba(227,28,121,.5)",
            "rgba(70,58,50,.5)",
        ],
        borderColor: 'rgba(121, 138, 146, 1)', // dark grey
        fontColor: "#353a40", // theUpsideDown color
        fontFamily: "'Lato-Regular', 'Helvetica Neue', 'Helvetica', 'Arial', 'sans-serif'", // Includes fallbacks (from default ChartJS config)
    };

    ownedByMeLegends = {
        Draft: "Draft",
        Awaiting_Acknowledgement: "Awaiting Acknowledgement",
        Overdue: "Overdue",
        Completed: "Completed",
        Scheduled: "Scheduled",
        Assigned: "Assigned"
    };

    assignedByMeLegends = {
        Awaiting_Acknowledgement: "Awaiting Acknowledgement",
        Overdue: "Overdue",
        Completed: "Completed",
        Scheduled: "Scheduled"
    };

    cXOneCoachingManagerAccess: boolean = false;

    constructor(private cvLocalizationService: CVLocalizationService,
        private service: CoachingStatusService,
        private zone: NgZone,
        private navService: NavigationService,
        private config: CvConfigurationService
    ) { }

    async ngOnInit() {
        this.moduleManager.getModuleInfo().DTO.SettingEmpSelector = false; // To render in settings model in coaching widget
        await this.moduleManager.forceLoadSettings();
        await this.cvLocalizationService.GetSection("Coaching_Status_Module");
        this.setComboMenu();
        this.moduleManager.setOptions({
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.Auto,
            name: 'Coaching Status',
            widgetMode: false,
            onSettingsApplied: settings => this.zone.run(() => this.loadCoachingData(false)),
            onRefresh: isAutoRefresh => this.zone.run(() => this.loadCoachingData(isAutoRefresh)),
            canRefresh: () => true,
            onExport: (format) => this.zone.run(() => this.service.LoadDataForExport(this.moduleKey, format)),
        });

        this.moduleManager.slideoutSettingsManager.subscribe(manager => {
            this.loadCoachingData(false);
        });

        this.cXOneCoachingManagerAccess = this.config.isCxOne ? this.moduleManager?.hasCXOneCoachingManagerAccess() ?? false : false;

    }

    async setComboMenu() {
        this.moduleManager.addMenuItem({
            name: "Legend_Position",
            icon: 'legend',
            type: ModuleManagerMenuItemType.SubMenu,
            getEnabled: () => true,
            subItems: [{
                name: "Top", // Menu items are localized automatically by Module Container.  Keys under section Module_Menu
                icon: '',
                click: () => this.setLegendPosition('top'),
            },
            {
                name: "Bottom",
                icon: '',
                click: () => this.setLegendPosition('bottom')
            },
            {
                name: "Left",
                icon: '',
                click: () => this.setLegendPosition('left')
            },
            {
                name: "Right",
                icon: '',
                click: () => this.setLegendPosition('right')
            }]
        } as ModuleManagerSubMenu);
        this.moduleManager.addState({
            name: 'chartState',
            autoLoad: true,
            getStateData: () => this.state,
            onStateLoaded: (stateData) => {
                if (!stateData)
                    return;

                this.state = stateData;
            }
        });

        await this.loadCoachingData(true);

        //Add Coaching Filter Component in toolbar
        this.moduleManager.addToolbarItem(CoachingTypeFilterComponent);
    }

    async setLegendPosition(position: string) {
        this.state.legendPosition = position;
        this.chartConfig.options.plugins.legend = { ...this.chartConfig.options.plugins.legend, ...{ position } };
        await this.moduleManager.saveState('chartState');
        this.moduleManager.requestPublishNeeded();
    }

    async setCoachingCategory(type) {
        this.coachingCategory = type;
        await this.createChart();
    }

    navigate() {
        const page = this.coachingCategory === 0 ? 'apps/#/cxpm-coaching/coaching-manager' : 'apps/#/cxpm-coaching/coaching-sessions';
        window.open(`${this.navService.cxoneTokenUrls.niceBaseUrl}/${page}`, "_blank");
    }

    async createChart() {

        this.data = this.coachingCategory === 0 ? this.ownedByMeData : this.assignedToMeData;
        // Check for and destroy any existing charts
        if (this.state == null || this.state.legendPosition == null) {
            this.state = {
                legendPosition: 'top',
                dataPadding: '5% 0 0 0'
            };
        }

        //
        // Make Chart
        //
        const chartLegends = this.coachingCategory === 0 ? this.ownedByMeLegends : this.assignedByMeLegends;
        const preparedLabels = [];
        for (const key in chartLegends) {
            if (chartLegends.hasOwnProperty(key)) {
                preparedLabels.push(this.cvLocalizationService.getCachedText("Coaching_Status_Module", key, chartLegends[key]));
            }
        }

        this.chartConfig =
            {
                type: 'doughnut',
                data: {
                    datasets: [{
                        data: [...[], ...this.calcWeightsWithMinSize(this.data)],
                        backgroundColor: this._chartConstants.colors,
                        borderColor: this._chartConstants.borderColor,
                        borderWidth: 1,
                        hoverBackgroundColor: this._chartConstants.colorsLight,
                    }],
                    labels: preparedLabels
                },
                options: {
                    cutout: '76%', // makes ring thicker and thinner 76 makes it 48 px thick
                    responsive: true,
                    maintainAspectRatio: false,
                    elements: {
                        center: {
                            text: "", // The actual content of the center is drawn by the beforeDraw custom plugin below
                            color: this._chartConstants.fontColor,
                            fontFamily: this._chartConstants.fontFamily,
                        },
                    },
                    plugins: {
                        emptyChart: {
                            disableCxone: this.config.isCxOne,
                        },
                        emptyDoughnut: this.data.Total === 0 ? {
                            color: '#D9D9D9',
                            width: 15,
                            radiusDecrease: 5
                        } : {},
                        tooltip: {
                            callbacks: {
                                title: (tooltipItemArray) => tooltipItemArray[0].label,
                                label: (tooltipItem) => {
                                    const numInState = this.getDataByIndex(tooltipItem.dataIndex);
                                    return ' ' + numInState + ' ';
                                },
                            },
                            titleFont: {
                                size: 16,
                            },
                            bodyFont: {
                                size: 14,
                            },
                            displayColors: true,
                        },
                        legend: {
                            display: true,
                            position: this.state.legendPosition,
                            labels: {
                                boxWidth: 13,
                                font: {
                                    family: this._chartConstants.fontFamily,
                                },
                                color: this._chartConstants.fontColor,
                                generateLabels: (chart) => { // adds custom labels to the chart legend
                                    const data = chart.data;
                                    if (data.labels.length && data.datasets.length) {
                                        return data.labels.map((label, i) => {
                                            const meta = chart.getDatasetMeta(0);
                                            const ds = data.datasets[0];
                                            const arc = meta.data[i];
                                            const custom = arc && arc.options || {};
                                            const arcOpts = chart.options.elements.arc;

                                            const fill = custom.backgroundColor ? custom.backgroundColor :
                                                resolve([ds.backgroundColor, arcOpts.backgroundColor], chart.ctx, i);
                                            const stroke = custom.borderColor ? custom.borderColor :
                                                resolve([ds.borderColor, arcOpts.borderColor], chart.ctx, i);
                                            const bw = custom.borderWidth ? custom.borderWidth :
                                                resolve([ds.borderWidth, arcOpts.borderWidth], chart.ctx, i);

                                            // We get the value of the current label
                                            const value = this.getDataByIndex(i);
                                            const toggle = chart.getDataVisibility(i);
                                            return {
                                                // Instead of `text: label,`
                                                // We add the value to the string
                                                text: label + " (" + value + ")",
                                                fillStyle: fill,
                                                strokeStyle: stroke,
                                                lineWidth: bw,
                                                hidden: !toggle,
                                                index: i
                                            };
                                        });
                                    }
                                },
                            }
                        },
                    },
                    layout: {
                        padding: {
                            left: 8,
                            right: 8,
                            top: 8,
                            bottom: 8
                        }
                    },
                    animation: {
                        duration: 0,
                    },
                    hover: {
                        animationDuration: 0,
                    },
                },
                plugins: [this.data.Total === 0 ? {
                    id: 'emptyDoughnut',
                    beforeDraw: chart => this.drawCenterLabel(chart),
                    afterDraw: (chart, args, options) => {
                        const { color, width, radiusDecrease } = options;
                        const { chartArea: { left, top, right, bottom }, ctx } = chart;
                        const centerX = (left + right) / 2;
                        const centerY = (top + bottom) / 2;
                        const r = Math.min(right - left, bottom - top) / 2;

                        ctx.beginPath();
                        ctx.lineWidth = width || 2;
                        ctx.strokeStyle = '#D9D9D9';
                        ctx?.arc(centerX, centerY, (r - radiusDecrease || 0), 0, 2 * Math.PI);
                        ctx.stroke();
                    }
                } :
                    {
                        id: "coaching-status-center-label",
                        beforeDraw: chart => this.drawCenterLabel(chart),
                    }],
            } as ChartConfiguration;
        this.chartConfig.data.datasets[0].data = [...[], ...this.calcWeightsWithMinSize(this.data)];
    }

    calcWeightsWithMinSize(dataObj) {
        const chartLegends = this.coachingCategory === 0 ? this.ownedByMeLegends : this.assignedByMeLegends;

        const threshold = 0.11; // Minimum weight of 11%
        const values = [];
        for (const key in chartLegends) {
            if (dataObj.hasOwnProperty(key)) {
                values.push(dataObj[key]);
            }
        }
        if (dataObj.FromApiSourcedRs) {
            values.push(dataObj.Waiting, dataObj.Preview);
        }

        const valuesSum = values.reduce((sum, v) => sum + v, 0);
        if (valuesSum <= 0) {
            for (let i = 0; i < values.length; i++) {
                values[i] = 0;
            }
        }
        const normalizedValues = values.map((v) => v / valuesSum);
        const aboveThresholdIndices = []; // List of indices (of the values array) that are above the threshold.  These will be reduced when there are values below the threshold
        let aboveThresholdSum = 0; // Sum of all the values above the threshold.  This is used to figure out the proportion to reduce each value by.
        let belowThresholdDiffSum = 0; // For values below the threshold, this variable is the sum of all the (threshold - value) differences
        for (let i = 0; i < normalizedValues.length; i++) {
            if (normalizedValues[i] > 0 && normalizedValues[i] < threshold) {
                belowThresholdDiffSum += threshold - normalizedValues[i];
                normalizedValues[i] = threshold;
            } else if (normalizedValues[i] > 0 && normalizedValues[i] > threshold) {
                aboveThresholdSum += normalizedValues[i];
                aboveThresholdIndices.push(i);
            }
        }
        // Proportionally reduce weights of those above the threshold.  Larger chunks get reduced more.
        aboveThresholdIndices.forEach(i => {
            const relativeSize = normalizedValues[i] / aboveThresholdSum;
            normalizedValues[i] -= relativeSize * belowThresholdDiffSum;
        });
        return normalizedValues;
    }

    getDataByIndex(index) {
        const chartLegends = this.coachingCategory === 0 ? this.ownedByMeLegends : this.assignedByMeLegends;
        const legendKeys = Object.keys(chartLegends);
        return this.data && this.data[legendKeys[index]] !== undefined ? this.data[legendKeys[index]] : 0;
    }

    drawCenterLabel(chart) {
        if (chart.config.options.elements.center) {
            //Get ctx from string
            const ctx = chart.ctx;

            //Get options from the center object in options
            const centerConfig = chart.config.options.elements.center;
            const fontStyle = centerConfig.fontStyle || this._chartConstants.fontFamily;

            // total the shown data
            let total = 0;
            try {
                if (this.chartObject) {
                    const datasetMeta = this.chartObject.chart.getDatasetMeta(0);
                    datasetMeta.data.forEach((data, i) => {
                        if (!this.chartObject.chart.getDataVisibility(i)) { // ignore hidden data
                            return;
                        }

                        const dataValue = this.getDataByIndex(i) || 0;
                        total += dataValue;
                    });
                } else {
                    total = this.data.Total;
                }
            } catch (e) { /* total will be 0 if no datasets */ }
            const txt = total.toString(); // agent Number
            let txt2 = this.cvLocalizationService.getCachedText("Coaching_Status_Module", "Total", "Total");
            if (total === 1) {
                txt2 = this.cvLocalizationService.getCachedText("Coaching_Status_Module", "Total", "Total");
            }
            const color = centerConfig.color || this._chartConstants.fontColor;
            const sidePadding = centerConfig.sidePadding || 20;
            const innerRadius = chart._metasets[0].data[0].innerRadius;

            const sidePaddingCalculated = (sidePadding / 100) * (innerRadius * 2);
            //Start with a base font of 30px
            ctx.font = "30px " + fontStyle;

            //Get the width of the string and also the width of the element minus 10 to give it 5px side padding
            const stringWidth2 = ctx.measureText(txt2).width;
            const elementWidth = (innerRadius * 2) - sidePaddingCalculated;

            // Find out how much the font can grow in width.
            const widthRatio2 = elementWidth / stringWidth2;

            const newFontSize = Math.floor(25 * widthRatio2); // using text to determine number size
            const newFontSize2 = Math.floor(newFontSize * 0.7);

            const elementHeight = (innerRadius * 2);

            // Pick a new font size so it will not be larger than the height of label.
            const fontSizeToUse = Math.min(newFontSize, elementHeight);
            const fontSizeToUse2 = Math.min(newFontSize2, elementHeight);

            // Set font settings to draw it correctly.
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const centerX = ((chart.chartArea.left + chart.chartArea.right) / 2);
            const centerY = ((chart.chartArea.top + chart.chartArea.bottom) / 2);
            ctx.font = fontSizeToUse + "px " + fontStyle;
            ctx.fillStyle = color;

            // Draw number in center
            ctx.fillText(txt, centerX, centerY - (fontSizeToUse2 / 2) - 6);

            ctx.font = fontSizeToUse2 + "px " + fontStyle;

            // Draw Agents text in center
            ctx.fillText(txt2, centerX, centerY + (fontSizeToUse2 / 2) + 6); // (fontSizeToUse2/2) + 5 is to offset slightly from center so texts stack
        }
    }

    async loadCoachingData(isAutoRefresh: boolean) {
        if (this.isDataLoading) {
            return; // don't have multiple outgoing requests
        }
        if (!isAutoRefresh) {
            this.moduleManager.showLoader = true;
        }
        this.isDataLoading = true;
        const abort = new Promise<void>((rslv, reject) => this.abortLoad = () => rslv());
        this.moduleManager.preventModuleMapUpdate();
        try {
            this.coachingData = await this.service.loadCoachingStatusData(this.moduleKey, abort);
            this.ownedByMeData = this.coachingData.OwnedByMeData;
            this.assignedToMeData = this.coachingData.AssignedToMeData;
            if (this.coachingData == null) {
                this.moduleManager.showLoader = false;
                return;
            } else {
                this.noData = false;
                await this.createChart();
            }
        } catch (e) {
            this.moduleManager.handleLoadError(e, isAutoRefresh);
        } finally {
            this.isDataLoading = false;
            this.moduleManager.setAutoRefreshReady();
            this.moduleManager.showLoader = false;
            this.moduleManager.removeModuleMapPrevention();
        }
    }
}
